import { FC, useState } from 'react';
import { usePathContext } from '@new/common/hooks/usePathContext';
import { Can, useAbility } from '@new/casl';

import { useSingleCourse } from '../contexts/SingleCourseProvider';
import { SingleCourseProvider } from '../contexts/SingleCourseProvider';

import { Tabs, TabItem } from '@new/common/ui/Tabs/Tabs';
import { EditButton } from '@new/common/ui/EditButton/EditButton';
import { PageHeader } from '@new/common/ui/PageHeader/PageHeader';
import { CourseDetailsTab } from '../components/CourseDetailsTab/CourseDetailsTab';
// import { CourseDiscussionsTab } from '../components/CourseDiscussionsTab/CourseDiscussionsTab';
import { CourseInstructorsTab } from '../components/CourseInstructorsTab/CourseInstructorsTab';
import { NotesPanel } from '@new/features/notes/components/NotesPanel/NotesPanel';
import { CourseCompletionSteps } from '../components/CourseCompletionSteps/CourseCompletionSteps';
import { Loader } from '@new/common/ui/Loader/Loader';
import { CourseObjectivesTab } from '../components/CourseObjectivesTab/CourseObjectivesTab';
import { CourseProjectTab } from '../components/CourseProjectTab/CourseProjectTab';
import { CourseDataStats } from '../components/CourseDataStats/CourseDataStats';

type CourseTab = 'details' | 'discussions' | 'admin' | 'objectives' | 'project' | 'stats';

export const SingleCoursePage: FC = () => {
  const { courseId = 0 } = usePathContext();

  return (
    <SingleCourseProvider courseId={courseId}>
      <SingleCoursePageContent />
    </SingleCourseProvider>
  );
};

const SingleCoursePageContent: FC = () => {
  const { course, activeStep } = useSingleCourse();
  const [selectedTab, setSelectedTab] = useState<CourseTab>('details');
  const ability = useAbility();

  if (!course) {
    return <Loader />;
  }

  const adminTab: TabItem<CourseTab> = {
    value: 'admin',
    label: 'Admin',
    content: <CourseInstructorsTab courseId={course.id} />,
  };

  const tabs: TabItem<CourseTab>[] = [
    {
      value: 'details',
      label: 'Course Details',
      content: <CourseDetailsTab course={course} />,
    },

    ...(ability.can('access', 'CourseObjectivesOverview')
      ? [
          {
            value: 'objectives' as CourseTab,
            label: 'Objectives',
            content: <CourseObjectivesTab courseId={course.id} />,
          },
        ]
      : []),
    ...(ability.can('access', 'CourseProjectTab')
      ? [
          {
            value: 'project' as CourseTab,
            label: 'Project',
            content: <CourseProjectTab course={course} />,
          },
        ]
      : []),
    // {
    //   value: 'discussions',
    //   label: 'Discussions',
    //   content: <CourseDiscussionsTab courseId={course.id} />,
    // },
    ...(ability.can('update', 'Course')
      ? [
          {
            value: 'stats' as CourseTab,
            label: 'Stats',
            content: <CourseDataStats courseId={course.id} />,
          },
        ]
      : []),
    ...(ability.can('update', 'Course') ? [adminTab] : []),
  ];

  return (
    <div className="flex min-h-full w-full min-w-0 flex-col gap-6 p-6 lg:p-8 pt-0!">
      <PageHeader
        title={course.name}
        description={course.shortDescription}
        badge={{ text: 'Course', bgColor: 'var(--secondary-B100)' }}
      />
      <Can I="update" a="Course">
        <EditButton path={`/courses/${course.id}/edit`} />
      </Can>
      {
        <div className="flex flex-col gap-6">
          <Can I="start" a="Course">
            <CourseCompletionSteps />
          </Can>
          {activeStep === 'self' && (
            <Tabs tabs={tabs} value={selectedTab} onValueChange={setSelectedTab} />
          )}
        </div>
      }
      <Can I="start" a="Course">
        <NotesPanel relId={course.id || 0} rel="course" />
      </Can>
    </div>
  );
};
